import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, User, Lock, Mail, Building, Grid, Calendar, Bell, Info } from 'lucide-react';
import { apiService } from '../services/api';
import Footer from '../components/Footer';

interface InvitationData {
  id: string;
  businessId: string;
  email: string;
  roleIds: string[];
  roleNames?: string[];
  status: string;
  token: string;
  expiresAt: string;
  businessName?: string;
  inviterName?: string;
}

interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

const InvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [existingUser, setExistingUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [step, setStep] = useState<'loading' | 'existing-user' | 'new-user' | 'success' | 'error'>('loading');
  
  // Form data for new user registration
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    acceptAgreement: false
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [submitting, setSubmitting] = useState(false);

  const getPostAcceptLoginPath = (inv: InvitationData | null) => {
    const roleNames = (inv?.roleNames || []).map(r => String(r).trim().toLowerCase());
    const isOwnerInvite = roleNames.includes('business owner') || roleNames.includes('owner');
    return isOwnerInvite ? '/login?type=business' : '/login?type=professional';
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setStep('error');
      setLoading(false);
      return;
    }

    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      // First, check if the invitation is valid and get invitation details
      const invitationData = await apiService.getInvitationByToken(token!);
      setInvitation(invitationData);
      
      // Check if user already exists
      const emailCheck = await apiService.checkEmailExists(invitationData.email);
      
      if (emailCheck.exists && emailCheck.user) {
        setExistingUser(emailCheck.user);
        setStep('existing-user');
      } else {
        setStep('new-user');
      }
    } catch (error: any) {
      console.error('Error loading invitation:', error);
      setError(error.response?.data?.message || 'Failed to load invitation');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUserAccept = async () => {
    if (!existingUser || !invitation) return;
    if (!formData.acceptAgreement) {
      setError('You must agree to the Professional Account Agreement to continue.');
      return;
    }
    
    setSubmitting(true);
    try {
      await apiService.acceptInvitation(invitation.token);
      setStep('success');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: {[key: string]: string} = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.password) errors.password = 'Password is required';
    if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!formData.acceptAgreement) errors.acceptAgreement = 'You must agree to the Professional Account Agreement';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await apiService.acceptInvitation(invitation!.token, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password
      });
      setStep('success');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value } as any));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Invitation</h2>
            <p className="text-gray-600">Please wait while we verify your invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invitation Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login?type=business')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Business Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const loginPath = getPostAcceptLoginPath(invitation);
    const isOwnerInvite = loginPath.includes('type=business');

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invitation Accepted!</h2>
            <p className="text-gray-600 mb-6">
              You have successfully joined {invitation?.businessName}. You can now log in to access your dashboard.
            </p>
            <button
              onClick={() => navigate(loginPath, { replace: true })}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isOwnerInvite ? 'Go to Business Sign In' : 'Go to Professional Sign In'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-6xl">
          <div className="md:flex md:gap-6 md:items-stretch">
            {/* Left side - Form */}
            <div className="md:w-1/2 md:flex md:flex-col mb-4 md:mb-0">
              <div className="bg-white rounded-lg shadow-md p-8 flex flex-col flex-1">
                {/* Header */}
                <div className="text-center mb-8">
                  <Building className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Join {invitation?.businessName}</h1>
                  <p className="text-gray-600">
                    {step === 'existing-user' 
                      ? `Welcome back, ${existingUser?.firstName}! Accept your invitation to join the team.`
                      : 'Create your account to accept this invitation.'
                    }
                  </p>
                </div>

        {/* Existing User Flow */}
        {step === 'existing-user' && existingUser && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <User className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <p className="font-medium text-blue-900">{existingUser.firstName} {existingUser.lastName}</p>
                  <p className="text-sm text-blue-700">{existingUser.email}</p>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptAgreement}
                  onChange={(e) => setFormData(prev => ({ ...prev, acceptAgreement: e.target.checked }))}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/agreements/professional" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Professional Account Agreement
                  </a>
                  <span className="text-red-500"> *</span>
                </span>
              </label>
            </div>
            
            <button
              onClick={handleExistingUserAccept}
              disabled={submitting || !formData.acceptAgreement}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          </div>
        )}

        {/* New User Flow */}
        {step === 'new-user' && (
          <form onSubmit={handleNewUserSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {formErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={invitation?.email || ''}
                  disabled
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Create a password"
                />
              </div>
              {formErrors.password && (
                <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
              </div>
              {formErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Professional Agreement */}
            <div>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptAgreement}
                  onChange={(e) => handleInputChange('acceptAgreement', e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/agreements/professional" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Professional Account Agreement
                  </a>
                  <span className="text-red-500"> *</span>
                </span>
              </label>
              {formErrors.acceptAgreement && (
                <p className="text-red-500 text-sm mt-1">{formErrors.acceptAgreement}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center text-red-600">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating Account...' : 'Create Account & Accept Invitation'}
            </button>
          </form>
        )}
              </div>
            </div>

            {/* Right side - Professional Account Info Card */}
            {(step === 'new-user' || step === 'existing-user') && (
              <div className="md:w-1/2 mt-4 md:mt-0 md:flex md:flex-col">
                <div className="bg-white rounded-lg shadow-md p-8 flex flex-col flex-1">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Professional account</h2>
                      <p className="text-sm text-gray-600">
                        You're here because a business invited you to join their team.
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium text-white bg-gray-500 flex items-center gap-1">
                      <span className="w-2 h-2 bg-white rounded-full"></span>
                      Invited
                    </span>
                  </div>

                  {/* Status Box */}
                  <div className="bg-gray-100 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <p className="text-sm text-gray-700">
                        Complete registration to access your professional tools and start managing your bookings.
                      </p>
                    </div>
                  </div>

                  {/* Features Section */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      What you can do with your professional account
                    </h3>
                    <div className="space-y-4">
                      {/* Feature 1: Choose services */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(37, 99, 235, 0.3)' }}>
                          <Grid className="h-5 w-5" style={{ color: '#2563eb' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Choose services you can perform</p>
                          <p className="text-sm text-gray-600">Turn services on/off so bookings match what you actually provide.</p>
                        </div>
                      </div>

                      {/* Feature 2: Manage schedule */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(37, 99, 235, 0.3)' }}>
                          <Calendar className="h-5 w-5" style={{ color: '#2563eb' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Manage your work schedule</p>
                          <p className="text-sm text-gray-600">Set availability, breaks, and time off so your calendar stays accurate.</p>
                        </div>
                      </div>

                      {/* Feature 3: Stay on top of appointments */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(37, 99, 235, 0.3)' }}>
                          <Calendar className="h-5 w-5" style={{ color: '#2563eb' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Stay on top of appointments</p>
                          <p className="text-sm text-gray-600">See your day at a glance, view details, and prepare for upcoming visits.</p>
                        </div>
                      </div>

                      {/* Feature 4: Get reminders */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(37, 99, 235, 0.3)' }}>
                          <Bell className="h-5 w-5" style={{ color: '#2563eb' }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-gray-900 mb-1">Get reminders and updates</p>
                          <p className="text-sm text-gray-600">Receive notifications when bookings change or customers reschedule.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Disclaimer */}
                  <div className="border-t border-gray-200 pt-4 mt-auto">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
                        <Info className="h-3 w-3 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-600">
                        Professionals can't create accounts on their own - your business controls invitations and permissions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default InvitationPage; 